import { Dialog } from "@headlessui/react"
import { FC, useEffect, useRef, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { ReleaseMarketing, User } from "../../../types"
import { KButton, KInput, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { lang } from "../../../constants/languages"
import { marketing } from "../../../apis"
import { mapListToCamelCase, mapToCamelCase } from "../../../utility"
import Loading from "../../ui/Loading"
import { toast } from "react-toastify"
import { successFilledToastOptions } from "../../../constants"

type MarketingProps = {
  user: User
}

const limit = 10

const FileUploadButton = ({ _key, isRelease, accept, addNewFile, releaseMarketingId }) => {
  const [recommendationModalOpen, setRecommendationModalOpen] = useState(false)

  const fileInputRef = useRef(null)

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      //@ts-ignore
      fileInputRef.current.click() // Triggers file input
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Call your function to handle the file
      addNewFile(_key, file, releaseMarketingId, isRelease)
    }
  }

  return (
    <>
      <KButton
        shadowDisabled={true}
        text={lang.marketing.upload}
        onClick={() => (_key.startsWith("video") ? setRecommendationModalOpen(true) : handleButtonClick())}
        background="transparent"
        padding="0px"
        width="auto"
      />
      <input type="file" ref={fileInputRef} style={{ display: "none" }} accept={accept} onChange={handleFileChange} />

      <Dialog open={recommendationModalOpen} onClose={() => setRecommendationModalOpen(false)}>
        <div
          className="fixed w-screen h-screen top-0 left-0 flex items-center justify-center bg-[#0000004d] "
          style={{ zIndex: 9999 }}
        >
          <div className="p-6 shadow-md rounded-[10px] bg-[#fff]">
            <KTitleSpan text={lang.marketing.video_recommendation_title} fontSize={20} lineHeight="20px" />
            <div className="mt-6 mb-8 grid gap-1">
              {lang.marketing.video_recommendations.map((recommendation) => (
                <KSpan text={`• ${recommendation}`} color="#111" />
              ))}
            </div>
            <div className="mt-2 flex justify-between">
              <KButton
                width="auto"
                onClick={() => setRecommendationModalOpen(false)}
                text={lang.common.cancel}
                background="transparent"
                shadowDisabled={true}
              />
              <KButton
                width="100px"
                onClick={() => {
                  handleButtonClick()
                  setRecommendationModalOpen(false)
                }}
                text={lang.common.confirm}
                background="black"
                textColor="white"
              />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}

const Marketing: FC<MarketingProps> = (props) => {
  const [loading, setLoading] = useState(true)
  const [fileLoading, setFileLoading] = useState(false)
  const [releaseMarketingList, setReleaseMarketingList] = useState<ReleaseMarketing[]>([])
  const [userReleaseMarketing, setUserReleaseMarketing] = useState<ReleaseMarketing>()
  const [timeoutId, setTimeoutId] = useState(undefined)
  const [searchText, setSearchText] = useState("")
  const [offset, setOffset] = useState<number>(0)

  const searchParams = new URLSearchParams(location.search)

  const search = (searchText: string) => {
    setSearchText(searchText)
    clearTimeout(timeoutId)

    setTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        setLoading(true)
        marketing(`/?offset=${0}&limit=${limit}&search=${searchText}`, "GET").then((resp) => {
          setOffset(0)
          setLoading(false)
          if (resp.data.results) setReleaseMarketingList(mapListToCamelCase(resp.data.results))
        })
      }, 800)
    )
  }

  useEffect(() => {
    setLoading(true)
    marketing(`/?type=user`, "GET").then((resp) => {
      //@ts-ignore
      if (resp.data.results?.length > 0) setUserReleaseMarketing(mapToCamelCase(resp.data.results[0]))

      const search = (searchParams && searchParams.get("search")) || ""
      const additionalParam = search ? `&search=${search}` : ""
      setSearchText(search)

      marketing(`/?type=release&limit=${limit}${additionalParam}`, "GET").then((resp) => {
        setLoading(false)
        if (resp.data.results?.length > 0) setReleaseMarketingList(mapListToCamelCase(resp.data.results))
      })
    })
  }, [])

  const onLoadMore = () => {
    const newOffset = offset + limit
    setOffset(newOffset)
    marketing(`/?type=release&offset=${newOffset}&limit=${limit}`, "GET").then((resp) => {
      if (resp.data.results?.length > 0) {
        //@ts-ignore
        setReleaseMarketingList([...releaseMarketingList, ...mapListToCamelCase(resp.data.results)])
      }
    })
  }

  const renderHeaders = (isRelease: boolean) => {
    return (
      <div className="flex gap-4" style={{ width: "100%" }}>
        <div className="flex" style={{ width: "22%" }}>
          <KTitleSpan
            text={isRelease ? lang.marketing.release : lang.marketing.artist_profile}
            fontSize={14}
            lineHeight="16px"
          />
        </div>
        <div className="flex" style={{ width: "1%" }}></div>
        <div className="flex" style={{ width: "10%" }}>
          <KTitleSpan text={lang.marketing.image1} fontSize={14} lineHeight="16px" />
        </div>
        <div className="flex" style={{ width: "10%" }}>
          <KTitleSpan text={lang.marketing.image2} fontSize={14} lineHeight="16px" />
        </div>
        <div className="flex" style={{ width: "10%" }}>
          <KTitleSpan text={lang.marketing.image3} fontSize={14} lineHeight="16px" />
        </div>
        <div className="flex" style={{ width: "1%" }}></div>
        <div className="flex" style={{ width: "10%" }}>
          <KTitleSpan text={lang.marketing.video1} fontSize={14} lineHeight="16px" />
        </div>
        <div className="flex" style={{ width: "10%" }}>
          <KTitleSpan text={lang.marketing.video2} fontSize={14} lineHeight="16px" />
        </div>
        {isRelease && <div className="flex" style={{ width: "1%" }}></div>}
        {isRelease && (
          <div className="flex" style={{ width: "10%" }}>
            <KTitleSpan text={lang.marketing.press_release} fontSize={14} lineHeight="16px" />
          </div>
        )}
      </div>
    )
  }

  const addNewFile = (_key: string, file: any, releaseMarketingId: number, isRelease: boolean) => {
    const formData = new FormData()
    formData.append("file", file)

    setFileLoading(true)
    marketing(`/upload_file/?id=${releaseMarketingId}&file_key=${_key}`, "POST", formData)
      .then((resp) => {
        setFileLoading(false)
        if (resp.data.file_id) {
          if (isRelease) {
            setReleaseMarketingList([
              ...releaseMarketingList.map((releaseMarketing) => {
                if (releaseMarketing.id === releaseMarketingId) {
                  let filteredKey = _key
                  if (_key === "press_release") filteredKey = "pressRelease"

                  releaseMarketing[filteredKey] = resp.data.file_id
                }
                return releaseMarketing
              })
            ])
          } else {
            //@ts-ignore
            setUserReleaseMarketing({ ...userReleaseMarketing, [_key]: resp.data.file_id })
          }
          toast.success(lang.marketing.success_upload, successFilledToastOptions)
        }
      })
      .catch(() => {
        setFileLoading(false)
        toast.success(lang.marketing.error_upload, successFilledToastOptions)
      })
  }

  const renderPill = (text: string) => {
    return (
      <div className="flex" style={{ borderRadius: 4, backgroundColor: "#F7F7F7", padding: 4 }} key={Math.random()}>
        <KSpan text={text} fontSize={11} fontWeight={500} lineHeight="12px" color="#111" letterSpacing="0.22px" />
      </div>
    )
  }

  const onResetDriveLink = (releaseMarketingId: number, _key: string, isRelease: boolean) => {
    if (isRelease) {
      setReleaseMarketingList([
        ...releaseMarketingList.map((releaseMarketing) => {
          if (releaseMarketing.id === releaseMarketingId) {
            releaseMarketing[_key] = null
          }
          return releaseMarketing
        })
      ])
    } else {
      //@ts-ignore
      setUserReleaseMarketing({ ...userReleaseMarketing, [_key]: null })
    }
  }

  const renderDriveIcon = (driveId: string, releaseMarketingId: number, _key: string, isRelease: boolean) => {
    return (
      <div className="flex gap-3">
        <KTooltip
          position="bottom"
          content={<KSpan text={lang.marketing.view} />}
          children={
            <div
              className="flex items-center cursor-pointer"
              onClick={() => window.open(`https://drive.google.com/file/d/${driveId}`, "_blank")}
            >
              <img src="/account_icons/eye.svg" width={20} />
            </div>
          }
        />
        <KTooltip
          position="bottom"
          width="90px"
          content={<KSpan text={lang.marketing.upload_again} />}
          children={
            <div
              className="flex items-center cursor-pointer"
              onClick={() => onResetDriveLink(releaseMarketingId, _key, isRelease)}
            >
              <img src="/catalog_icons/upload.svg" width={20} />
            </div>
          }
        />
      </div>
    )
  }

  const renderUserProfileView = () => {
    return (
      <div className="flex mt-4 gap-4" style={{ width: "100%" }}>
        <div className="flex gap-2 items-center" style={{ width: "22%" }}>
          <img
            src={props.user?.photo || "/reports_icons/avatar.svg"}
            alt="profile-photo"
            className="min-w-14 min-h-14 max-h-14 max-w-14"
            style={{ borderRadius: 5 }}
          />
          <div className="grid">
            <KSpan text={`${props.user.firstName} ${props.user.lastName}`} color="#111" fontWeight={500} />
            {renderPill(props.user.role?.name || "")}
          </div>
        </div>
        <div className="flex" style={{ width: "1%" }}></div>
        <div className="flex items-center" style={{ width: "10%" }}>
          {userReleaseMarketing?.image1 ? (
            renderDriveIcon(userReleaseMarketing.image1, userReleaseMarketing.id, "image1", false)
          ) : (
            <FileUploadButton
              _key={"image1"}
              isRelease={false}
              accept={".jpeg,.jpg"}
              addNewFile={addNewFile}
              releaseMarketingId={userReleaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex items-center" style={{ width: "10%" }}>
          {userReleaseMarketing?.image2 ? (
            renderDriveIcon(userReleaseMarketing.image2, userReleaseMarketing.id, "image2", false)
          ) : (
            <FileUploadButton
              _key={"image2"}
              isRelease={false}
              accept={".jpeg,.jpg"}
              addNewFile={addNewFile}
              releaseMarketingId={userReleaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex items-center" style={{ width: "10%" }}>
          {userReleaseMarketing?.image3 ? (
            renderDriveIcon(userReleaseMarketing.image3, userReleaseMarketing.id, "image3", false)
          ) : (
            <FileUploadButton
              _key={"image3"}
              isRelease={false}
              accept={".jpeg,.jpg"}
              addNewFile={addNewFile}
              releaseMarketingId={userReleaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex" style={{ width: "1%" }}></div>
        <div className="flex items-center" style={{ width: "10%" }}>
          {userReleaseMarketing?.video1 ? (
            renderDriveIcon(userReleaseMarketing.video1, userReleaseMarketing.id, "video1", false)
          ) : (
            <FileUploadButton
              _key={"video1"}
              isRelease={false}
              accept={"video/*"}
              addNewFile={addNewFile}
              releaseMarketingId={userReleaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex items-center" style={{ width: "10%" }}>
          {userReleaseMarketing?.video2 ? (
            renderDriveIcon(userReleaseMarketing.video2, userReleaseMarketing.id, "video2", false)
          ) : (
            <FileUploadButton
              _key={"video2"}
              isRelease={false}
              accept={"video/*"}
              addNewFile={addNewFile}
              releaseMarketingId={userReleaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex" style={{ width: "1%" }} />
        <div className="flex" style={{ width: "10%" }} />
      </div>
    )
  }

  const renderReleaseView = (releaseMarketing: ReleaseMarketing) => {
    const totalTrack = releaseMarketing.release?.totalTrack || 0
    const songText = totalTrack === 1 ? lang.common.song_text_single : lang.common.song_text_multi
    return (
      <div className="flex mt-2 gap-4 items-center" style={{ width: "100%" }} key={releaseMarketing.id}>
        <div className="flex items-center gap-2" style={{ width: "22%" }}>
          <img
            src={releaseMarketing.release?.coverFileUrl || "/reports_icons/avatar.svg"}
            alt="profile-photo"
            className="min-w-14 min-h-14 max-h-14 max-w-14"
            style={{ borderRadius: 5 }}
          />
          <div className="grid">
            <KSpan text={`${releaseMarketing?.release?.title}`} color="#111" fontWeight={500} />
            <div className="flex gap-1">
              {renderPill(releaseMarketing.release?.productionYear || "")}
              {renderPill(`${totalTrack.toString()} ${songText}`)}
            </div>
          </div>
        </div>
        <div className="flex" style={{ width: "1%" }}></div>
        <div className="flex " style={{ width: "10%" }}>
          {releaseMarketing?.image1 ? (
            renderDriveIcon(releaseMarketing.image1, releaseMarketing.id, "image1", true)
          ) : (
            <FileUploadButton
              _key={"image1"}
              isRelease={true}
              accept={".jpeg,.jpg"}
              addNewFile={addNewFile}
              releaseMarketingId={releaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex" style={{ width: "10%" }}>
          {releaseMarketing?.image2 ? (
            renderDriveIcon(releaseMarketing.image2, releaseMarketing.id, "image2", true)
          ) : (
            <FileUploadButton
              _key={"image2"}
              isRelease={true}
              accept={".jpeg,.jpg"}
              addNewFile={addNewFile}
              releaseMarketingId={releaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex" style={{ width: "10%" }}>
          {releaseMarketing?.image3 ? (
            renderDriveIcon(releaseMarketing.image3, releaseMarketing.id, "image3", true)
          ) : (
            <FileUploadButton
              _key={"image3"}
              isRelease={true}
              accept={".jpeg,.jpg"}
              addNewFile={addNewFile}
              releaseMarketingId={releaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex" style={{ width: "1%" }}></div>
        <div className="flex" style={{ width: "10%" }}>
          {releaseMarketing?.video1 ? (
            renderDriveIcon(releaseMarketing.video1, releaseMarketing.id, "video1", true)
          ) : (
            <FileUploadButton
              _key={"video1"}
              isRelease={true}
              accept={"video/*"}
              addNewFile={addNewFile}
              releaseMarketingId={releaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex" style={{ width: "10%" }}>
          {releaseMarketing?.video2 ? (
            renderDriveIcon(releaseMarketing.video2, releaseMarketing.id, "video2", true)
          ) : (
            <FileUploadButton
              _key={"video2"}
              isRelease={true}
              accept={"video/*"}
              addNewFile={addNewFile}
              releaseMarketingId={releaseMarketing?.id}
            />
          )}
        </div>
        <div className="flex" style={{ width: "1%" }} />
        <div className="flex" style={{ width: "10%" }}>
          {releaseMarketing?.pressRelease ? (
            renderDriveIcon(releaseMarketing.pressRelease, releaseMarketing.id, "pressRelease", true)
          ) : (
            <FileUploadButton
              _key={"press_release"}
              isRelease={true}
              accept={".docx,.pdf"}
              addNewFile={addNewFile}
              releaseMarketingId={releaseMarketing?.id}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="catalog flex-grow mt-6">
      {(fileLoading || loading) && <Loading />}
      <div className="flex justify-between items-center mt-10">
        <div className="items-center grid gap-2">
          <KTitleSpan text={lang.marketing.header} fontSize={24} lineHeight="26px" />
          <KSpan text={lang.marketing.subtitle} fontSize={16} lineHeight="18px" />
        </div>
        <div>
          <KInput
            onChange={(value) => search(value)}
            value={searchText}
            placeholder={lang.catalog.filter_components.search_release_text_placeholder}
            leftIcon="/catalog_icons/search-grey.svg"
            padding="14px"
            background="#F7F7F7"
            hoverBackground="#fff"
          />
        </div>
      </div>
      <div className="grid items-center mt-8">
        {renderHeaders(false)}
        {renderUserProfileView()}
      </div>
      <div className="mt-8 pt-8" style={{ borderTop: "1px grey dashed" }}>
        {renderHeaders(true)}
        <div className="mb-4" />
        {releaseMarketingList.map((releaseMarketing: ReleaseMarketing) => {
          return renderReleaseView(releaseMarketing)
        })}
      </div>
      <div className="mt-4 flex justify-center">
        {releaseMarketingList.length === offset + limit && (
          <KButton text={lang.marketing.load_more} onClick={() => onLoadMore()} background="transparent" width="auto" />
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(Marketing)
